import React, { useState } from 'react';
import {
  FormControl,
  FormLabel,
  Input,
  VStack,
  useTheme,
  Heading,
  Alert,
  AlertIcon,
  useToast,
} from '@chakra-ui/react';
import { TextG14 } from '../../../components/typography';
import { Place } from '../../../types/article';
import { BlueButton } from '../../../components/button';
import { updatePlace} from '../../../endpoints/api';
import {ToastMessage} from "../../../components/toast";

interface ContactFormProps {
  place: Place;
  refetchPlace: () => void;
  onClose?: () => void;
}

const EditForm: React.FC<ContactFormProps> = ({ place, refetchPlace, onClose }) => {
  const toast = useToast();
  const theme = useTheme();

  const [phone, setPhone] = useState(place.phone);
  const [website, setWebsite] = useState(place.website);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    
    try {
      await updatePlace(place.id!, {
        phone,
        website,
      }).catch(e => {
        ToastMessage({
          status: 'error',
          text: e.message
        });
      });

      await refetchPlace();

      toast({
        title: "Fields updated",
        status: "success",
        duration: 5000,
        isClosable: true,
      });
      
      if (onClose) onClose();
    } catch (err) {
      if(err instanceof Error) {
        setError(err?.message);
        return;
      }

      setError('Failed to submit your request. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack spacing={0} align="stretch">
      <VStack as="form" onSubmit={handleSubmit} spacing={4} align="stretch" p={4}>
        <Heading mt="0 !important" size="md" color={theme.colors.blue} mb={1}>
          Update some place fields
        </Heading>

        {error && (
          <Alert status="error" borderRadius="md">
            <AlertIcon />
            {error}
          </Alert>
        )}

        <FormControl id="phone" isRequired>
          <FormLabel fontSize="sm">Phone number</FormLabel>
          <Input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Phone number"
            borderColor={theme.colors.lightGrey}
            _focus={{borderColor: theme.colors.blue}}
            isDisabled={isSubmitting}
          />
        </FormControl>

        <FormControl id="website" isRequired>
          <FormLabel fontSize="sm">Website</FormLabel>
          <Input
            value={website}
            onChange={(e) => setWebsite(e.target.value)}
            placeholder="Website"
            borderColor={theme.colors.lightGrey}
            _focus={{borderColor: theme.colors.blue}}
            isDisabled={isSubmitting}
          />
        </FormControl>

        <BlueButton
          mt={6}
          type="submit"
          width="100%"
          height="48px"
          isLoading={isSubmitting}
          disabled={isSubmitting}>
          <TextG14 fontWeight="500" color="white">
            Update
          </TextG14>
        </BlueButton>
      </VStack>
    </VStack>
  );
};

export default EditForm;
